/**
 * Constants shared between the React client and the Express server.
 * Anything here must be safe to import from both environments (no Node-only
 * APIs, no DOM-only APIs).
 */

export const COOKIE_NAME = "footagemgr_session";
export const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
// Sessions are invalidated after this much time without an authenticated
// request. Enforced both client-side (inactivity warning + logout) and
// server-side (rejecting requests whose users.last_signed_in is older).
export const INACTIVITY_LIMIT_MS = 48 * 60 * 60 * 1000;
export const UNAUTHED_ERR_MSG = "UNAUTHORIZED";
export const AXIOS_TIMEOUT_MS = 30_000;

// Patient clips tagged with any of these Activity Tags get the same Bedrock
// transcription treatment as provider A-Roll — a .txt and .srt sidecar
// saved alongside the video in Drive and (when available) locally.
// Matching is case-insensitive.
export const TRANSCRIBE_TRIGGER_ACTIVITY_TAGS = ["Explanation", "Discussion"] as const;

const TRANSCRIBE_TRIGGER_LOWER = new Set<string>(
  TRANSCRIBE_TRIGGER_ACTIVITY_TAGS.map((t) => t.toLowerCase()),
);

/** True when at least one of the file's activity tags is in the trigger set. */
export function activitiesTriggerTranscription(activities: readonly string[]): boolean {
  return activities.some((a) => TRANSCRIBE_TRIGGER_LOWER.has(a.trim().toLowerCase()));
}

// ─── Audio file support ──────────────────────────────────────────────────────
// Audio files are first-class citizens in the upload + sidecar + search
// pipeline. They skip Pegasus (which is video-only) and get their
// Description by Claude-summarizing the Whisper transcript instead; their
// transcript text feeds the same `transcript_text` / `transcript_srt`
// columns as video. Match detection (audio_video_matches) then links each
// audio file to any video clips in the same subject scope whose transcripts
// embed close enough to count as the "same moment captured twice."

/** Audio file extensions accepted by the uploader. Lowercase, with the dot. */
export const AUDIO_FILE_EXTENSIONS = [
  ".mp3", ".wav", ".m4a", ".aac", ".flac", ".ogg", ".opus",
] as const;

/** File-input `accept` fragment — also matches MIME `audio/*`. */
export const AUDIO_ACCEPT_FRAGMENT =
  `audio/*,${AUDIO_FILE_EXTENSIONS.join(",")}`;

const AUDIO_EXT_SET = new Set<string>(AUDIO_FILE_EXTENSIONS);

/**
 * True when the given filename OR MIME type indicates an audio file.
 * Pass `{ name, type }` from a `File`, or just a string filename.
 * Browsers don't always populate `type` reliably (e.g. .opus on Safari),
 * so we check both axes.
 */
export function isAudioFile(input: string | { name: string; type?: string }): boolean {
  const name = typeof input === "string" ? input : input.name;
  const type = typeof input === "string" ? "" : (input.type ?? "");
  if (type.toLowerCase().startsWith("audio/")) return true;
  const lower = name.toLowerCase();
  const dot = lower.lastIndexOf(".");
  if (dot < 0) return false;
  return AUDIO_EXT_SET.has(lower.slice(dot));
}

/** Media-kind discriminator persisted on `footage_files.media_kind`. */
export type MediaKind = "video" | "audio";

/** Decide media_kind from a file. Anything not recognized as audio is "video". */
export function mediaKindForFile(input: string | { name: string; type?: string }): MediaKind {
  return isAudioFile(input) ? "audio" : "video";
}
